import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Building2, Check, Mail, MapPin, RotateCw, Timer, Users, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePersistentState } from '../lib/usePersistentState';
import { useScrollOnOpen } from '../lib/useScrollOnOpen';
import { daysAt, RowHousePhoto, type FlowRow } from './AcceptedFlowShared';
import { ClaimAtrBox, type AtrAccepted } from './ClaimAtrBox';
import { ClaimEmailBox } from './ClaimEmailBox';
import { ClaimChat } from './ClaimChat';
import { Module } from './ui/Module';
import { RChip } from './ui/RChip';
import { DecisionBar } from './ui/DecisionBar';
// (ClaimChat + ClaimAtrBox usados en Review ATR / Manager Review; ClaimEmailBox en ATR Follow up.)

/** Cabecera colapsada compartida: nombre + #claim + seguro (izquierda), equipo, días. */
function AtrHeader({ r, open, onToggle, onClient }: { r: FlowRow; open: boolean; onToggle: () => void; onClient: (id: string) => void }) {
  const days = daysAt(r.status_since);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
      <button onClick={onToggle} style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, marginTop: 2, font: 'inherit' }}>
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{open ? '▾' : '▸'}</span>
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => onClient(r.claim_id)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--ink)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>            {r.homeowner_name || r.claim_number || `#${r.claim_id}`}
          </button>
          {r.claim_number && <Link to={`/claims/${r.claim_id}`} onClick={(e) => e.stopPropagation()} className="badge gray" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)', textDecoration: 'none' }}>{r.claim_number}</Link>}
          {r.insurance_company && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--bg)', color: 'var(--ink)' }}><Building2 size={12} strokeWidth={1.75} /> {r.insurance_company}</span>}
        </div>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>
          {r.contractor_name || '—'}{r.address ? <> · <MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.address}</> : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--accent-soft)', color: 'var(--accent)' }}><Users size={12} strokeWidth={1.75} /> {r.assigned_team_name || '—'}</span>
          {days != null && <RChip variant={days >= 7 ? 'no' : 'info'} icon={Timer}>{days}d</RChip>}
        </div>
      </div>
    </div>
  );
}

async function decide(claimId: string, decision: string): Promise<string | null> {
  try { await api.post(`/api/claims/${claimId}/claims-decision`, { decision }); return null; }
  catch (e) { return e instanceof ApiError ? e.message : 'Error'; }
}

/** Fila de Review ATR (y Manager Review): IZQ video del ATR (aceptar el que se usa) + botones,
 *  DER módulo de email (el link del video aceptado se incluye; enviarlo al adjuster mueve a ATR
 *  Follow up), ABAJO los department updates. */
function ReviewAtrRow({ r, onChanged, onClient, view }: { r: FlowRow; onChanged: () => void; onClient: (id: string) => void; view: 'cards' | 'lines' }) {
  const { t } = useI18n();
  const [open, setOpen] = usePersistentState<boolean>(`open:atr:${r.claim_id}`, false);
  const rowRef = useScrollOnOpen<HTMLDivElement>(open);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState<AtrAccepted | null>(null);
  const act = async (decision: string, confirmMsg?: string) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true); const err = await decide(r.claim_id, decision); setBusy(false);
    if (err) alert(err); else onChanged();
  };
  // Enviar el email al ADJUSTER (tab insurance) con un video aceptado → avanza a ATR Follow up.
  const onEmailSent = async (tab: string) => {
    if (tab !== 'insurance' || !accepted) return;
    const err = await decide(r.claim_id, 'atr_send');
    if (err) alert(err); else onChanged();
  };
  return (
    <div ref={rowRef} className="card" style={{ padding: 12, scrollMarginTop: 80 }}>
      {view === 'cards' && <RowHousePhoto url={r.photo_url} />}
      <AtrHeader r={r} open={open} onToggle={() => setOpen((o) => !o)} onClient={onClient} />
      {open && (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 10, paddingTop: 12, display: 'grid', gap: 12 }}>
          {/* Fila 1: IZQUIERDA videos + botones · DERECHA email. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12, alignItems: 'start' }}>
            {/* Izquierda: videos del ATR (aceptar el que se usa) + Request new / Manager review. */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ClaimAtrBox claimId={r.claim_id} selectable onSelectionChange={setAccepted} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 130 }}>
                {/* Ambos mueven el claim de etapa → sólidos de decisión (rediseño). */}
                <button className="btn sm solid-warn" disabled={busy} onClick={() => act('atr_request', t('atr.requestNewConfirm'))}><RotateCw size={14} strokeWidth={1.75} /> {t('atr.requestNew')}</button>
                <button className="btn sm solid-blue" disabled={busy} onClick={() => act('atr_manager_review')}><Briefcase size={14} strokeWidth={1.75} /> {t('atr.managerReview')}</button>
              </div>
            </div>
            {/* Derecha: email al adjuster (con el link del video aceptado). Enviarlo avanza el status. */}
            <ClaimEmailBox
              claimId={r.claim_id} departmentKey="claims" title={t('atr.emailToAdjuster')}
              atrVideo={accepted ? { url: accepted.videoUrl, label: accepted.label } : null}
              onSent={onEmailSent}
            />
          </div>
          {/* Abajo: department updates (canal Claims) a todo el ancho. */}
          <ClaimChat claimId={r.claim_id} channel="claims" title={t('cwa.updates')} />
        </div>
      )}
    </div>
  );
}

/** Fila de ATR Follow up: seguimiento al seguro + botones Approved / ATR Rejected. */
function FollowUpAtrRow({ r, onChanged, onClient, view }: { r: FlowRow; onChanged: () => void; onClient: (id: string) => void; view: 'cards' | 'lines' }) {
  const { t } = useI18n();
  const [open, setOpen] = usePersistentState<boolean>(`open:atrfu:${r.claim_id}`, false);
  const rowRef = useScrollOnOpen<HTMLDivElement>(open);
  const [busy, setBusy] = useState(false);
  const act = async (decision: string, label: string) => {
    if (!window.confirm(`${t('cwa.confirm')} "${label}"?`)) return;
    setBusy(true); const err = await decide(r.claim_id, decision); setBusy(false);
    if (err) alert(err); else onChanged();
  };
  return (
    <div ref={rowRef} className="card" style={{ padding: 12, scrollMarginTop: 80 }}>
      {view === 'cards' && <RowHousePhoto url={r.photo_url} />}
      <AtrHeader r={r} open={open} onToggle={() => setOpen((o) => !o)} onClient={onClient} />
      {open && (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 10, paddingTop: 12, display: 'grid', gap: 12 }}>
          {/* Follow-up unificado en Department Updates (ClaimChat) — reemplaza el módulo aparte. */}
          <ClaimChat claimId={r.claim_id} channel="claims" title={t('cwa.updates')} />
          <ClaimAtrBox claimId={r.claim_id} />
          <Module id={`email-insurance:${r.claim_id}`} icon={Mail} title={t('atr.emailToInsurance')} preview={t('mod.emailPreview')}>
            <div style={{ paddingTop: 2 }}><ClaimEmailBox claimId={r.claim_id} departmentKey="claims" /></div>
          </Module>
          {/* Barra de decisión: los sólidos quedan reservados a mover el claim de etapa. */}
          <div className="module">
            <DecisionBar label={t('cwa.decision')} hint={t('atr.followUpHint')}>
              <button className="btn sm solid-good" disabled={busy} onClick={() => act('atr_approved', t('cwa.approved'))}><Check size={14} strokeWidth={1.75} /> {t('cwa.approved')}</button>
              <button className="btn sm solid-bad" disabled={busy} onClick={() => act('atr_rejected', t('atr.rejected'))}><X size={14} strokeWidth={1.75} /> {t('atr.rejected')}</button>
            </DecisionBar>
          </div>
        </div>
      )}
    </div>
  );
}

/** Secciones de Claims para el flujo ATR: Review ATR y ATR Follow up. */
export function ClaimsAtrSection(
  { rows, stage, onChanged, onClient, view = 'lines' }:
  { rows: FlowRow[]; stage: 'Review ATR' | 'ATR Follow up' | 'Manager Review'; onChanged: () => void; onClient: (id: string) => void; view?: 'cards' | 'lines' },
) {
  const { t } = useI18n();
  if (rows.length === 0) return <div className="page-empty">{t('cwa.empty')}</div>;
  const wrap: React.CSSProperties = view === 'cards'
    ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12, alignItems: 'start' }
    : { display: 'grid', gap: 10 };
  // Manager Review usa la misma UI que Review ATR (video + botones + updates).
  const Row = stage === 'ATR Follow up' ? FollowUpAtrRow : ReviewAtrRow;
  return <div style={wrap}>{rows.map((r) => <Row key={r.presence_id} r={r} onChanged={onChanged} onClient={onClient} view={view} />)}</div>;
}
